import { useApiClient } from '@/composables/useApiClient'
import { useToast } from 'primevue'
import { useI18n } from 'vue-i18n'
import type { ICreateShortcut, IUpdateShortcut, MessageShortcutDto } from './interfaces'

export const useShortcutService = () => {
  const privateApi = useApiClient(true)
  const toast = useToast()
  const { t } = useI18n()

  const showToast = (type: 'success' | 'error', messageKey: string) => {
    toast.add({
      severity: type,
      summary: t(type === 'success' ? 'general.success' : 'general.error'),
      detail: t(messageKey),
      life: 3000,
    })
  }

  const handleError = (error: unknown, messageKey: string) => {
    showToast('error', messageKey)
  }

  /**
   * Devuelve los atajos de la campana (PUBLIC) mas los privados del agente.
   * El endpoint no pagina: responde la lista completa.
   *
   * Propaga el error a proposito. Devolver [] al fallar hacia que el store
   * diera la campana por cargada y el agente se quedaba sin atajos hasta
   * cambiar de campana y volver.
   */
  const getOperShortcuts = async (
    campaign_id: string,
    user_id?: string,
  ): Promise<MessageShortcutDto[]> => {
    return await privateApi.get<MessageShortcutDto[]>('/oper/shortcuts', {
      params: { campaign_id, user_id },
    })
  }

  const createShortcut = async (data: ICreateShortcut): Promise<MessageShortcutDto> => {
    try {
      const response = await privateApi.post<MessageShortcutDto>('/shortcuts', data)
      showToast('success', 'shortcut.success_created')
      return response
    } catch (error) {
      handleError(error, 'shortcut.error_creating_shortcut')
      throw error
    }
  }

  const updateShortcut = async (
    shortcut_id: string,
    data: IUpdateShortcut,
  ): Promise<MessageShortcutDto> => {
    try {
      const response = await privateApi.patch<MessageShortcutDto>(`/shortcuts/${shortcut_id}`, data)
      showToast('success', 'shortcut.success_updated')
      return response
    } catch (error) {
      handleError(error, 'shortcut.error_updating_shortcut')
      throw error
    }
  }

  return {
    getOperShortcuts,
    createShortcut,
    updateShortcut,
  }
}
